/**
 * LiveBotDemo.js  —  v34.99 (Feb 13 2026)
 *
 * Auto-playing animated demo of CARTIE inside Microsoft Teams.
 * Built specifically because hackathon viewers said "I don't get how
 * it works in practice". Solves that in 22 seconds of motion.
 *
 * Sequence loops every ~25s:
 *   0s   — Empty channel
 *   1s   — User asks "@Cartie why did my OpenAI bill spike?"
 *   3s   — Typing indicator
 *   5s   — Bot answers (diagnosis + cause + amount burned)
 *   9s   — Bot adds PR link card
 *   14s  — Bot adds savings + CFO briefing note
 *   22s  — Pause, then loop
 *
 * Visual style mimics Teams chat (sky-blue accent, white-on-slate
 * bubbles, @ mentions). Switch to Slack styling by setting
 * `platform="slack"` — same logic, violet accent, /commands.
 */
import React, { useEffect, useState } from 'react';
import { MessageSquare, Slack, Sparkles, GitPullRequest, Check, FileText, AlertTriangle, RotateCcw } from 'lucide-react';

const STEPS = [
  { id: 'empty',         atMs: 0    },
  { id: 'user_question', atMs: 1500 },
  { id: 'typing',        atMs: 3000 },
  { id: 'bot_diagnosis', atMs: 5000 },
  { id: 'bot_pr',        atMs: 9000 },
  { id: 'bot_savings',   atMs: 14000 },
  { id: 'pause',         atMs: 22000 },
  { id: 'reset',         atMs: 25000 },
];

const PLATFORM_STYLE = {
  teams: {
    label: 'Microsoft Teams',
    accent: 'sky',
    text: 'text-sky-300',
    pillBg: 'bg-sky-500/10',
    pillBorder: 'border-sky-500/25',
    bubbleSelf: 'bg-sky-500',
    bubbleSelfText: 'text-white',
    userTrigger: '@Cartie',
    Icon: MessageSquare,
    statusColor: 'bg-emerald-400',
  },
  slack: {
    label: 'Slack',
    accent: 'violet',
    text: 'text-violet-300',
    pillBg: 'bg-violet-500/10',
    pillBorder: 'border-violet-500/25',
    bubbleSelf: 'bg-violet-500',
    bubbleSelfText: 'text-white',
    userTrigger: '/cartie',
    Icon: Slack,
    statusColor: 'bg-emerald-400',
  },
};

const LiveBotDemo = ({ platform = 'teams' }) => {
  const cfg = PLATFORM_STYLE[platform];
  const [stepIdx, setStepIdx] = useState(0);
  const [tick, setTick] = useState(0); // forces re-render to retrigger animation when looping

  useEffect(() => {
    const timeouts = STEPS.map((step) =>
      setTimeout(() => {
        if (step.id === 'reset') {
          setStepIdx(0);
          setTick((t) => t + 1);
        } else {
          setStepIdx(STEPS.findIndex((s) => s.id === step.id));
        }
      }, step.atMs)
    );
    return () => timeouts.forEach(clearTimeout);
  }, [tick]);

  const reached = (id) => STEPS.findIndex((s) => s.id === id) <= stepIdx;
  const showTyping = reached('typing') && !reached('bot_diagnosis');
  const replay = () => { setStepIdx(0); setTick((t) => t + 1); };

  return (
    <div className="rounded-2xl border border-white/10 bg-slate-950/80 overflow-hidden shadow-2xl" data-testid="live-bot-demo">
      {/* Header bar — looks like a Teams/Slack channel header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-white/[0.03]">
        <div className="flex items-center gap-2.5">
          <div className={`inline-flex items-center justify-center w-7 h-7 rounded-md ${cfg.pillBg} ring-1 ${cfg.pillBorder}`}>
            <cfg.Icon className={`w-4 h-4 ${cfg.text}`} />
          </div>
          <div>
            <div className="text-sm font-semibold text-white flex items-center gap-1.5">
              #finops-alerts
              <span className={`relative flex h-1.5 w-1.5`}>
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${cfg.statusColor} opacity-70`} />
                <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${cfg.statusColor}`} />
              </span>
              <span className="text-[10px] font-medium text-emerald-300">LIVE DEMO</span>
            </div>
            <div className="text-[10px] text-slate-500">{cfg.label} · 14 members</div>
          </div>
        </div>
        <button
          onClick={replay}
          className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-white transition-colors px-2 py-1 rounded-md hover:bg-white/5"
          data-testid="live-bot-demo-replay"
        >
          <RotateCcw className="w-3 h-3" />
          Replay
        </button>
      </div>

      {/* Chat area */}
      <div className="px-4 py-5 min-h-[420px] space-y-3" key={tick}>
        {/* 1. User question */}
        {reached('user_question') && (
          <Bubble side="right" name="You" testid="demo-msg-user" animate>
            <div className="text-sm">
              <span className={cfg.text + ' font-semibold'}>{cfg.userTrigger}</span> why did my OpenAI bill spike yesterday?
            </div>
          </Bubble>
        )}

        {/* 2. Typing indicator */}
        {showTyping && (
          <Bubble side="left" name="CARTIE" cfg={cfg} testid="demo-msg-typing" animate>
            <div className="flex items-center gap-1.5 py-1">
              <Dot d={0} />
              <Dot d={200} />
              <Dot d={400} />
            </div>
          </Bubble>
        )}

        {/* 3. Bot diagnosis */}
        {reached('bot_diagnosis') && (
          <Bubble side="left" name="CARTIE" cfg={cfg} testid="demo-msg-diagnosis" animate>
            <div className="text-sm space-y-2">
              <div className="flex items-center gap-1.5 text-rose-300 font-semibold">
                <AlertTriangle className="w-3.5 h-3.5" />
                Cost spike confirmed
              </div>
              <div className="text-slate-200 leading-relaxed">
                Your OpenAI bill jumped <span className="font-bold text-rose-300">4.2&times;</span> over 14 minutes &mdash; peak <span className="font-mono">$1,847/hr</span>.
              </div>
              <div className="rounded-md bg-slate-900/60 border border-white/10 px-3 py-2 mt-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Root cause</div>
                <div className="font-mono text-xs text-slate-200">
                  Recursive loop in <span className="text-emerald-300">api/stream.py:42</span>
                </div>
                <div className="text-[11px] text-slate-400 mt-1">
                  Already burned: <span className="font-bold text-rose-300">$48,300</span>
                </div>
              </div>
            </div>
          </Bubble>
        )}

        {/* 4. PR card */}
        {reached('bot_pr') && (
          <Bubble side="left" name="CARTIE" cfg={cfg} testid="demo-msg-pr" animate>
            <div className="text-sm space-y-2">
              <div className="text-slate-200">I opened a pull request with an 11-line fix:</div>
              <div className="rounded-lg border border-white/10 bg-slate-900/60 px-3 py-2.5 hover:border-emerald-500/30 transition-colors cursor-pointer">
                <div className="flex items-center gap-2 mb-1">
                  <GitPullRequest className="w-3.5 h-3.5 text-emerald-300" />
                  <span className="text-xs font-semibold text-emerald-300">PR #847</span>
                  <span className="text-[10px] text-slate-500">·</span>
                  <span className="text-[10px] text-slate-500">2 min ago</span>
                </div>
                <div className="text-sm text-white font-medium mb-1.5">Add depth-cap to streaming retry loop</div>
                <div className="flex items-center gap-3 text-[10px] text-slate-400">
                  <span className="text-emerald-400">+8</span>
                  <span className="text-rose-400">-3</span>
                  <span>api/stream.py</span>
                  <span className="text-slate-500">·</span>
                  <span className="text-emerald-300">CI passing</span>
                </div>
              </div>
            </div>
          </Bubble>
        )}

        {/* 5. Savings + CFO briefing */}
        {reached('bot_savings') && (
          <Bubble side="left" name="CARTIE" cfg={cfg} testid="demo-msg-savings" animate>
            <div className="text-sm space-y-2">
              <div className="flex items-center gap-1.5 text-emerald-300 font-semibold">
                <Check className="w-3.5 h-3.5" />
                Estimated savings: $42,800 / month
              </div>
              <div className="text-slate-200 text-[13px] leading-relaxed">
                One-paragraph briefing sent to <span className={cfg.text + ' font-semibold'}>#finance</span>. Your CFO will see this in their morning summary.
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-slate-500 pt-1">
                <FileText className="w-3 h-3" />
                Audit trail: PR → CFO Slack → financial-controls log
              </div>
            </div>
          </Bubble>
        )}

        {/* Bottom-of-chat indicator */}
        {!reached('bot_savings') && stepIdx > 0 && (
          <div className="text-center pt-3">
            <span className="inline-flex items-center gap-1.5 text-[10px] text-slate-500">
              <Sparkles className="w-3 h-3 text-emerald-300 animate-pulse" />
              Live demo &mdash; auto-plays every 25 seconds
            </span>
          </div>
        )}
      </div>

      {/* Footer / input bar (visual only) */}
      <div className="px-4 py-3 border-t border-white/10 bg-white/[0.02] flex items-center gap-2">
        <div className="flex-1 px-3 py-2 rounded-md bg-slate-900/80 border border-white/10 text-[12px] text-slate-500">
          Try it yourself when the bot ships &mdash; join the waitlist below ↓
        </div>
        <div className={`inline-flex items-center justify-center w-8 h-8 rounded-md ${cfg.pillBg} ring-1 ${cfg.pillBorder}`}>
          <cfg.Icon className={`w-3.5 h-3.5 ${cfg.text}`} />
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const Bubble = ({ side = 'left', name, cfg, children, testid, animate }) => (
  <div
    className={`flex items-end gap-2 ${side === 'right' ? 'flex-row-reverse' : ''} ${animate ? 'animate-fade-in-up' : ''}`}
    style={animate ? { animation: 'fadeInUp 320ms ease-out both' } : undefined}
    data-testid={testid}
  >
    <div className={`flex flex-col ${side === 'right' ? 'items-end' : ''}`}>
      <span className={`text-[10px] mb-0.5 ${side === 'right' ? 'text-slate-400' : (cfg ? cfg.text : 'text-emerald-300')}`}>
        {name}
        {side === 'left' && <span className="ml-1 px-1 py-px rounded bg-emerald-500/15 text-emerald-300 text-[9px] font-semibold">BOT</span>}
      </span>
      <div
        className={`max-w-[90%] sm:max-w-[480px] px-3.5 py-2.5 rounded-2xl ${
          side === 'right'
            ? `${cfg?.bubbleSelf || 'bg-sky-500'} ${cfg?.bubbleSelfText || 'text-white'} rounded-br-md`
            : 'bg-white/[0.05] border border-white/10 text-slate-100 rounded-bl-md'
        }`}
      >
        {children}
      </div>
    </div>
  </div>
);

const Dot = ({ d }) => (
  <span
    className="block w-1.5 h-1.5 rounded-full bg-slate-400 opacity-60"
    style={{ animation: `bounceDot 900ms ${d}ms infinite ease-in-out` }}
  />
);

// CSS keyframes (inline so we don't need a global stylesheet edit)
const InlineStyles = () => (
  <style>{`
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes bounceDot {
      0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
      40%           { transform: translateY(-4px); opacity: 1; }
    }
  `}</style>
);

// Wrap export so styles inject once
const LiveBotDemoWithStyles = (props) => (
  <>
    <InlineStyles />
    <LiveBotDemo {...props} />
  </>
);

export default LiveBotDemoWithStyles;
